import { parseContractEndDate } from '@order-management/dates';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  AdminDeviceTableRow,
  AdminLicenseKeyExpandableTableRow,
  AdminLicenseKeyTableRow,
  AdminSalesOrderTableRow,
  AvailabilityStatus,
} from '@order-management/types';
import {
  getAssignedSoftware,
  getDeviceName,
} from '../../device-groups/utils';
import Link from '../../ui/link/link';
import { Column } from '../../ui/table/types';

export const adminDevicesTableColumns: Column<AdminDeviceTableRow>[] = [
  {
    key: 'serialNumber.number',
    title: 'Serial number',
    className: 'is-width-20',
    render: (el) => {
      let deviceIdentifier = el.serialNumber?.number;
      if (!deviceIdentifier) {
        deviceIdentifier = el.name ?? el.defaultName;
      }
      return <Link to={`/devices/${el.id}`}>{deviceIdentifier}</Link>;
    },
    sortable: false,
  },
  {
    key: 'deviceGroup.name',
    title: 'Device group',
    className: 'is-width-20',
    render: (el) => el.deviceGroup?.name,
    sortable: true,
  },
  {
    key: 'name',
    title: 'Device name',
    className: 'is-width-10',
    render: (el) => getDeviceName(el),
  },
  {
    key: 'model.name',
    title: 'Device type',
    className: 'is-width-10',
    render: (el) => el.model?.displayName,
  },
  {
    key: 'registrationStatus',
    title: 'Status',
    className: 'is-width-5',
    render: (el) => el.registrationStatus,
  },
  {
    key: 'software',
    title: 'Assigned software',
    className: 'is-width-35',
    render: (el) => getAssignedSoftware(el.assignments),
  },
];

export const adminSalesOrdersTableColumns: Column<AdminSalesOrderTableRow>[] = [
  {
    key: 'number',
    title: 'Sales Order',
    className: 'is-width-20',
    render: (el) => (
      <Link to={`sales-orders/${el.id}`}>
        {formatSalesOrderNumber(el.number)}
      </Link>
    ),
    sortable: true,
  },
  {
    key: 'endUser',
    title: 'End user',
    className: 'is-width-35',
    render: (el) => el.endUser,
    sortable: true,
  },
  {
    key: 'softwareTitles',
    title: 'Software titles',
    className: 'is-width-15',
    render: (el) => el.softwareTitles,
    sortable: true,
  },
  {
    key: 'totalSeats',
    title: 'Total seats',
    className: 'is-width-15',
    render: (el) => el.totalSeats,
    sortable: true,
  },
  {
    key: 'totalDevices',
    title: 'Devices',
    className: 'is-width-15',
    render: (el) => el.totalDevices,
    sortable: true,
  },
];

// Admin tools show every license key, so the software details page is opened
// with the filters needed to show the same keys pre-applied
export const buildAdminSoftwareDetailsUrl = (
  licensingProductGroupId: string,
  salesOrderNumber?: number
) => {
  const searchParams = new URLSearchParams({
    availabilityStatus: AvailabilityStatus.ALL,
    includeUnrelated: 'true',
  });

  if (salesOrderNumber !== undefined) {
    searchParams.set('salesOrders', salesOrderNumber.toString());
  }

  return `software/${licensingProductGroupId}?${searchParams}`;
};

export const adminLicenseKeyTableColumns = (
  isExpandableRow: (row: AdminLicenseKeyTableRow) => boolean,
  selectedSalesOrderNumber?: number
): Column<AdminLicenseKeyTableRow, AdminLicenseKeyExpandableTableRow>[] => [
  {
    key: 'softwareTitle',
    title: 'Software title',
    className: 'is-width-30',
    render: (el) => (
      <Link
        to={buildAdminSoftwareDetailsUrl(
          el.licensingProductGroupId,
          selectedSalesOrderNumber
        )}
      >
        {el.softwareTitle}
      </Link>
    ),
    sortable: true,
  },
  {
    key: 'licenseKey',
    title: 'License key',
    className: 'is-width-30',
    render: (el) => {
      if (isExpandableRow(el)) {
        return null;
      }
      return el.licenseKey;
    },
    expandableContentRender: (el) => el.licenseKey,
    sortable: false,
  },
  {
    key: 'assignedSeats',
    title: 'Assigned seats',
    className: 'is-width-10',
    render: (el) => el.assignedSeats,
    expandableContentRender: (el) => el.assignedSeats,
    sortable: true,
  },
  {
    key: 'unassignedSeats',
    title: 'Unassigned seats',
    className: 'is-width-10',
    render: (el) => el.unassignedSeats,
    expandableContentRender: (el) => el.unassignedSeats,
    sortable: true,
  },
  {
    key: 'totalSeats',
    title: 'Total seats',
    className: 'is-width-10',
    render: (el) => el.totalSeats,
    expandableContentRender: (el) => el.totalSeats,
    sortable: true,
  },
  {
    key: 'contractEndDate',
    title: 'Contract end date',
    className: 'is-width-10',
    render: (el) => parseContractEndDate(el.contractEndDate),
    sortable: true,
  },
];
